import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";
import { getPreflightUploadConstraints } from "../../lib/preflight-upload-constraints";

export type UploadDropzoneHandle = {
  openFilePicker: () => void;
};

export interface UploadDropzoneProps {
  /** Receives accepted+rejected files; validation/queueing happens in the manager. */
  onFiles: (files: FileList | File[]) => void;
  /** Comma-separated `accept` attribute for the file picker. */
  accept?: string;
  disabled?: boolean;
  /** Override the browse affordance label (e.g. when creatives are already queued). */
  buttonLabel?: string;
  /** Allow selecting more than one file at once. Defaults to true. */
  multiple?: boolean;
  /** Override the dropzone headline (defaults to preflight copy). */
  titleText?: string;
  /** Override the constraints line under the headline (defaults to preflight copy). */
  constraintsText?: string;
  className?: string;
  /**
   * `label` — whole zone opens the picker (preflight hub default).
   * `button` — only the browse control opens the picker; avoids label re-activation
   * quirks after the native dialog closes (partner production upload).
   */
  browseTrigger?: "label" | "button";
}

const PREFLIGHT_UPLOAD = getPreflightUploadConstraints();

const SMOOTH_EASE = "ease-[cubic-bezier(0.16,1,0.3,1)]";

const ZONE_CLASS = cn(
  "flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-input-border bg-muted/20 px-6 py-8 text-center",
  "transition-[border-color,background-color] duration-500 motion-reduce:transition-none",
  SMOOTH_EASE,
  "group-hover:border-primary/25 group-hover:bg-muted/30",
);

const ICON_BOX_CLASS = cn(
  "border-border bg-background flex size-11 items-center justify-center rounded-md border will-change-transform",
  "transition-[border-color,background-color,transform,box-shadow] duration-500 motion-reduce:transition-none motion-reduce:will-change-auto",
  SMOOTH_EASE,
  "group-hover:-translate-y-1 group-hover:scale-[1.04] group-hover:border-primary/40 group-hover:bg-primary-subtle/25 group-hover:shadow-sm",
  "motion-reduce:group-hover:translate-y-0 motion-reduce:group-hover:scale-100",
);

const ICON_CLASS = cn(
  "text-muted-foreground transition-colors duration-500 motion-reduce:transition-none",
  SMOOTH_EASE,
  "group-hover:text-primary motion-reduce:group-hover:text-muted-foreground",
);

const ICON_BOX_ACTIVE = "border-primary/40 bg-primary-subtle/25 -translate-y-1 scale-[1.04] shadow-sm";
const ICON_ACTIVE = "text-primary";

const BROWSE_CLASS =
  "border-border bg-background text-foreground inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-sm border px-2.5 text-xs font-normal whitespace-nowrap";

/**
 * Drop target wired to a multi-select file input. The whole zone opens the native
 * picker (label association); dragging files over highlights the zone. File-type/
 * size validation is the upload manager's responsibility — this only forwards files.
 */
export const UploadDropzone = forwardRef<UploadDropzoneHandle, UploadDropzoneProps>(
function UploadDropzone(
  {
    onFiles,
    accept = PREFLIGHT_UPLOAD.accept,
    disabled = false,
    buttonLabel,
    multiple = true,
    titleText,
    constraintsText,
    className,
    browseTrigger = "label",
  },
  ref,
) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function openFilePicker() {
    if (disabled) return;
    inputRef.current?.click();
  }

  useImperativeHandle(ref, () => ({ openFilePicker }), [disabled]);

  function handleFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (files && files.length > 0) onFiles(files);
    event.target.value = "";
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    if (event.dataTransfer.files.length > 0) onFiles(event.dataTransfer.files);
  }

  function handlePaste(event: ClipboardEvent<HTMLElement>) {
    if (disabled || event.clipboardData.files.length === 0) return;
    event.preventDefault();
    onFiles(event.clipboardData.files);
  }

  const browseLabel = buttonLabel ?? t("preflight.dropzone.button");
  const resolvedTitle = titleText ?? t("preflight.dropzone.title");
  const resolvedConstraints =
    constraintsText ??
    t("preflight.dropzone.constraints", {
      formats: PREFLIGHT_UPLOAD.formats,
      imageMax: PREFLIGHT_UPLOAD.imageMax,
      videoMax: PREFLIGHT_UPLOAD.videoMax,
    });

  const content = (
    <>
      <span className={cn(ICON_BOX_CLASS, isDragging && ICON_BOX_ACTIVE)}>
        <Icon
          name="upload"
          size="sm"
          className={cn(ICON_CLASS, isDragging && ICON_ACTIVE)}
        />
      </span>
      <div className="flex flex-col gap-1">
        <p className="text-foreground text-sm font-medium">{resolvedTitle}</p>
        <p className="text-muted-foreground text-xs">{resolvedConstraints}</p>
      </div>
      {browseTrigger === "button" ? (
        <button
          type="button"
          className={BROWSE_CLASS}
          onClick={(event) => {
            event.stopPropagation();
            openFilePicker();
          }}
        >
          <Icon name="upload" size="xs" aria-hidden />
          {browseLabel}
        </button>
      ) : (
        <span className={BROWSE_CLASS}>
          <Icon name="upload" size="xs" aria-hidden />
          {browseLabel}
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={handleFiles}
      />
    </>
  );

  const zoneProps = {
    "aria-label": t("preflight.dropzone.label"),
    onDragOver: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: handleDrop,
    onPaste: handlePaste,
    tabIndex: disabled ? -1 : 0,
    className: cn(
      ZONE_CLASS,
      browseTrigger === "label" && "group cursor-pointer",
      browseTrigger === "button" && "group",
      isDragging && "border-primary bg-primary-subtle/60",
      className,
    ),
  };

  if (disabled) {
    return (
      <div {...zoneProps} aria-disabled="true" className={cn(zoneProps.className, "opacity-50")}>
        {content}
      </div>
    );
  }

  if (browseTrigger === "button") {
    return (
      <div {...zoneProps} role="group">
        {content}
      </div>
    );
  }

  return (
    <label
      {...zoneProps}
      role="button"
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        openFilePicker();
      }}
    >
      {content}
    </label>
  );
});
